import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Buildings, ChatCircle, ChartLine, DeviceMobile, Lightning, Globe, MagnifyingGlass,
  Code, CheckSquare, ShieldCheck, Palette, CloudArrowUp, FileText, Headphones, ChartBar,
  CheckCircle, Sparkle
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import HeroProposalComposer from '../components/HeroProposalComposer';
import SavingsCalculator from '../components/SavingsCalculator';
import { SectionHeading, StatsRow, CTABanner, IndustryGrid } from '../components/SiteBlocks';
import { SERVICES, AI_AGENTS, INDUSTRIES, STATS, TESTIMONIALS } from '../data/content';

const SVC_ICONS = {
  Buildings, ChatCircle, ChartLine, DeviceMobile, Lightning, Globe, MagnifyingGlass,
  Code, CheckSquare, ShieldCheck, Palette, CloudArrowUp, FileText, Headphones, ChartBar,
};

export default function HomePage() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "XceedAgents.ai",
    "url": "https://xceedagents.ai",
    "description": "Hybrid AI + senior engineering team. Tell us what you need built. AI generates your proposal in 60 seconds.",
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO
        path="/"
        title="Build With Us — AI Agents + Senior Engineering Teams"
        description="XceedAgents.ai is your build partner for AI agents, enterprise products, websites and mobile apps. From free AI proposal to interactive prototype, real production code, AI-verified testing and a release-ready package — one platform, end to end."
        structuredData={structuredData}
      />
      <Navbar />

      {/* ============== RUNABLE-STYLE HERO: ONE BIG PROMPT ============== */}
      <section className="relative overflow-hidden" style={{ background: 'var(--dark-section-bg)', color: 'var(--dark-section-text)' }}>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(234,88,12,0.18), transparent 45%), radial-gradient(circle at 80% 80%, rgba(13,148,136,0.14), transparent 40%)'
        }} />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-16 lg:pt-32 lg:pb-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-5 inline-flex items-center gap-2" style={{ color: '#FB923C' }}>
                <Sparkle weight="fill" size={14} /> Build With Us · AI Agents × Senior Engineers
              </div>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
              data-testid="hero-heading"
            >
              What do you want to <span className="gradient-text">build</span>?
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-5 text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed"
              style={{ color: 'var(--dark-section-muted)' }}
              data-testid="hero-subtitle"
            >
              Describe it once. Our AI agents and senior engineers take it from idea to launch —
              free proposal in 60 seconds, interactive prototype, real production code, tested and released.
            </motion.p>

            <div className="mt-10">
              <HeroProposalComposer centered />
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
              className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm"
              style={{ color: '#94A3B8' }}
              data-testid="hero-trust-row"
            >
              {['Hybrid AI × Human teams', 'MVPs in 4-6 weeks', '50-70% cost savings', '100% code ownership'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle weight="fill" size={14} style={{ color: '#FB923C' }} /> {t}
                </span>
              ))}
            </motion.div>
          </div>

          {/* ============== IDEA → LAUNCH JOURNEY STRIP ============== */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-14 rounded-2xl border p-6 lg:p-8"
            style={{ borderColor: 'var(--dark-section-border)', background: 'rgba(15,23,42,0.6)' }}
            data-testid="journey-strip"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#FB923C' }}>
                One platform · idea to launch
              </div>
              <Link to="/workspace" className="text-sm font-semibold inline-flex items-center gap-1 hover:underline" style={{ color: '#FB923C' }} data-testid="journey-workspace-link">
                Open your Workspace <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { icon: FileText, t: 'AI Proposal', d: 'Scope, timeline & cost + PDF', cost: 'Free' },
                { icon: Palette, t: 'Prototype', d: 'Clickable multi-page product', cost: 'Teaser free · 150 cr' },
                { icon: Code, t: 'Development', d: 'Real React + FastAPI + MongoDB code', cost: '500 cr' },
                { icon: CheckSquare, t: 'Testing & QA', d: 'AI-verified quality report', cost: '200 cr' },
                { icon: CloudArrowUp, t: 'Release', d: 'Hosting-ready package', cost: '100 cr' },
              ].map((s, i) => (
                <div key={i} className="relative" data-testid={`journey-step-${i + 1}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(234,88,12,0.15)' }}>
                      <s.icon size={18} weight="duotone" style={{ color: '#FB923C' }} />
                    </div>
                    <span className="font-heading text-xs font-bold" style={{ color: '#64748B' }}>0{i + 1}</span>
                  </div>
                  <div className="font-semibold text-sm" style={{ color: '#F8FAFC' }}>{s.t}</div>
                  <div className="text-xs mt-1 leading-snug" style={{ color: '#94A3B8' }}>{s.d}</div>
                  <div className="text-[11px] font-bold mt-2 uppercase tracking-wide" style={{ color: '#FB923C' }}>{s.cost}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============== TRUSTED BY ============== */}
      <section className="border-y" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-6">
          <div className="text-sm font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Trusted by teams at</div>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-4 opacity-80">
            {['Acme Corp', 'StellarHealth', 'Vantage', 'BloomDirect', 'NorthCloud', 'Helios.io'].map(n => (
              <div key={n} className="font-heading text-lg font-semibold tracking-tight" style={{ color: 'var(--text-secondary)' }}>{n}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SERVICES OVERVIEW ============== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Agentic Workflow Services"
            title="One team. Every kind of agent."
            subtitle="From WhatsApp bots to enterprise ERP automations to full-stack products — we ship 60% faster with our hybrid AI + senior-engineer model."
          />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => {
              const Icon = SVC_ICONS[s.icon] || Sparkle;
              return (
                <Link
                  key={s.id}
                  to={`/services/${s.id}`}
                  className="group block p-7 rounded-2xl border bg-white hover-lift transition-all hover:shadow-elevated"
                  style={{ borderColor: 'var(--border)' }}
                  data-testid={`service-card-${s.id}`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'var(--tag-bg)' }}>
                    <Icon size={24} weight="duotone" style={{ color: 'var(--primary-accent)' }} />
                  </div>
                  <div className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: 'var(--primary-accent)' }}>{s.shortName}</div>
                  <h3 className="font-heading text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{s.name}</h3>
                  <p className="text-sm mb-4 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{s.summary}</p>
                  <span className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: 'var(--primary-accent)' }}>
                    Explore <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== AI AGENTS ROW ============== */}
      <section className="dark-section py-20 lg:py-28" style={{ background: 'var(--dark-section-bg)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Meet Our AI Agents"
            title="8 specialist AI agents. Embedded in every engagement."
            subtitle="Every senior engineer at XceedAgents works alongside 8 specialist AI agents — for code, tests, reviews, UI polish, DevOps, docs, support, and analytics. That's how we deliver 60% faster."
            dark
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {AI_AGENTS.map((a) => {
              const Icon = SVC_ICONS[a.icon] || Sparkle;
              return (
                <Link
                  key={a.id}
                  to={`/ai-agents/${a.id}`}
                  className="group block p-6 rounded-xl border transition-all hover:shadow-elevated"
                  style={{ borderColor: 'var(--dark-section-border)', background: 'var(--dark-section-alt)' }}
                  data-testid={`agent-card-${a.id}`}
                >
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(234,88,12,0.15)' }}>
                    <Icon size={20} weight="duotone" style={{ color: a.color }} />
                  </div>
                  <h4 className="font-heading text-lg font-bold mb-1" style={{ color: 'var(--dark-section-text)' }}>{a.name}</h4>
                  <div className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: a.color }}>{a.role}</div>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-section-muted)' }}>{a.summary}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== STATS ============== */}
      <StatsRow stats={STATS} />

      {/* ============== INDUSTRIES ============== */}
      <section className="py-20 lg:py-28" style={{ background: 'var(--background)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Industries"
            title="Deep expertise in the verticals you care about."
            subtitle="From regulated finance to high-volume e-commerce to clinical healthcare — we've shipped in every major industry."
          />
          <div className="mt-12">
            <IndustryGrid items={INDUSTRIES} />
          </div>
        </div>
      </section>

      {/* ============== OFFSHORE OUTSOURCING BANNER ============== */}
      <section className="py-20 lg:py-24" style={{ background: 'var(--surface)' }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: 'var(--primary-accent)' }}>India Outsourcing · Done Right</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
              Build with India's top 1% — augmented by AI agents.
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Get senior engineers from India for 50-70% less than US/UK rates — augmented with 8 specialist AI agents that make every developer 3-5× more productive. Choose your engagement model: dedicated team (ODC), staff augmentation, fixed-price project, or managed services.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/offshore-outsourcing">
                <Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="offshore-cta">
                  Explore Offshore Models <ArrowRight size={16} weight="bold" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="h-12 px-7 font-semibold" data-testid="pricing-cta">See Pricing</Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: '$8.5K', l: 'Per engineer / month — Senior, dedicated' },
              { v: '60%', l: 'Cost savings vs US/UK rates' },
              { v: '2-3 wk', l: 'To first engineer onboarded' },
              { v: '12 mo', l: 'No-rotation guarantee on ODCs' },
            ].map((s, i) => (
              <div key={i} className="p-5 rounded-xl border bg-white" style={{ borderColor: 'var(--border)' }}>
                <div className="font-heading text-3xl font-bold" style={{ color: 'var(--primary-accent)' }}>{s.v}</div>
                <div className="text-sm mt-2 leading-snug" style={{ color: 'var(--text-secondary)' }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SAVINGS CALCULATOR ============== */}
      <SavingsCalculator />

      {/* ============== TESTIMONIALS ============== */}
      <section className="py-20 lg:py-28" style={{ background: 'var(--background)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="What clients say"
            title="Real outcomes. Real partnerships."
          />
          <div className="mt-12 grid lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="p-7 rounded-2xl border bg-white" style={{ borderColor: 'var(--border)' }}>
                <div className="text-3xl font-heading font-bold" style={{ color: 'var(--primary-accent)' }}>"</div>
                <p className="mt-2 text-base leading-relaxed" style={{ color: 'var(--text-primary)' }}>{t.quote}</p>
                <div className="mt-5 pt-5 border-t" style={{ borderColor: 'var(--border)' }}>
                  <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{t.name}</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.role} · {t.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <CTABanner
        title="Ready to build with AI + senior engineers?"
        subtitle="Tell our AI what you need. We'll send you a detailed proposal with scope, timeline, and cost in 60 seconds. No signup required."
        ctaText="Request for Proposal"
        ctaHref="/start-project"
        secondaryText="Talk to Sales"
        secondaryHref="/contact"
      />

      <Footer />
    </div>
  );
}
